import { FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import { getPlanType } from 'features/Onboarding/common/planUtils';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';
import { TopBar } from 'features/Onboarding/modules/StepsPricingPlan/components/StepsLayout';

import hasDiscount from '../../lib/hasDiscount';
import { OnboardingPlanDisplay } from '../../types';
import PlanPrice from '../PlanPrice';
import locales from './locales';

interface PlanOptionProps {
  condensed?: boolean;
  plan: OnboardingPlanDisplay;
  isButtonLoading: boolean;
  isMobile?: boolean;
  onChoose: (plan: OnboardingPlanDisplay) => void;
}

const PlanOption: FC<PlanOptionProps> = ({
  condensed,
  isButtonLoading,
  isMobile,
  onChoose,
  plan,
}) => {
  const priceValue = amountWithMinorCurrency(
    hasDiscount(plan) && plan.discountPrice !== null
      ? plan.discountPrice
      : plan.price,
  );

  const nextToPrice = () => {
    if (!plan.providerPackageId) {
      return locales.formatString(locales.perMonth, {
        taxMention: locales.vat.excluded,
      });
    }

    if (plan.discountPrice === plan.price) {
      return locales.vat.excluded;
    }

    return (
      <>
        {locales.formatString(locales.insteadOf, {
          taxMention: locales.vat.excluded,
        })}{' '}
        {plan.planPrice ? (
          <span style={{ textDecorationLine: 'line-through' }}>
            {formatAmount({
              forcePlusSign: false,
              value: amountWithMinorCurrency(plan.price),
              withNoMinorUnitDigits: Number.isInteger(
                amountWithMinorCurrency(plan.planPrice),
              ),
            })}
          </span>
        ) : null}
      </>
    );
  };

  return (
    <Box>
      <TopBar.Title>{locales.title}</TopBar.Title>
      {condensed ? null : (
        <Text
          fontSize="font-18"
          fontWeight="500"
          marginBottom="space-6"
          variant="primary"
        >
          {plan.label}
        </Text>
      )}
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="end">
          <PlanPrice
            value={priceValue}
            withoutCents={Number.isInteger(priceValue)}
          />
          <Text marginBottom="6px" marginLeft="space-8" size="sm">
            {nextToPrice()}
          </Text>
        </Flex>
        {isMobile && !plan.providerPackageId ? (
          <SunshineTag colorScheme="blue" icon="gift" size="sm">
            {plan.trialPeriod}{' '}
            {locales.formatString(locales.trialPeriod, {
              plural: plan.trialPeriod > 1 ? 's' : '',
            })}
          </SunshineTag>
        ) : null}
      </Flex>
      <Flex direction="column" marginTop="auto" paddingTop="space-24">
        <Button
          data-testid={`button-${getPlanType(plan.providerPlanId)}`}
          isLoading={isButtonLoading}
          onClick={() => onChoose(plan)}
          variant="primary"
          width="100%"
        >
          {locales.choseButton + plan.brandName}
        </Button>

        {!isMobile && !plan.providerPackageId && !condensed ? (
          <SunshineTag
            colorScheme="blue"
            icon="gift"
            marginX="auto"
            marginY="space-16"
            size="sm"
          >
            {plan.trialPeriod}{' '}
            {locales.formatString(locales.trialPeriod, {
              plural: plan.trialPeriod > 1 ? 's' : '',
            })}
          </SunshineTag>
        ) : null}
        {plan.providerPackageId && plan.planPrice ? (
          <Text
            justifyContent="center"
            marginX="auto"
            marginY="space-8"
            variant="secondary"
          >
            {locales.formatString(locales.pricePerMonth, {
              price: formatAmount({
                forcePlusSign: false,
                value: amountWithMinorCurrency(plan.planPrice),
                withNoMinorUnitDigits: Number.isInteger(
                  amountWithMinorCurrency(plan.planPrice),
                ),
              }),
              taxMention: locales.vat.excluded,
            })}
          </Text>
        ) : null}
      </Flex>
    </Box>
  );
};

export default PlanOption;
